import React, { useCallback, useEffect, useMemo, useState } from 'react';
import countries from 'i18n-iso-countries';
import enCountries from 'i18n-iso-countries/langs/en.json';
import Select from 'react-select';
import { Globe, Save, Trash2 } from 'lucide-react';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

countries.registerLocale(enCountries as any);

type GeoAccessPolicy = 'FULL_ACCESS' | 'NO_ORDERS' | 'BLOCK_ALL';

type GeoAccessRule = { countryCode: string; policy: GeoAccessPolicy };
type GeoAccessConfigV1 = {
  version: 1;
  defaultPolicy: GeoAccessPolicy;
  strictUnknownBlock: boolean;
  staffBypass: boolean;
  countries: GeoAccessRule[];
};

type CountryOption = { value: string; label: string };

function normalizeCountryCode(input: string): string {
  return String(input || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);
}

function countryNameForCode(code: string): string {
  const iso2 = normalizeCountryCode(code);
  if (!iso2) return '';
  const name = countries.getName(iso2, 'en', { select: 'official' }) as string | undefined;
  return name || iso2;
}

export default function GeoAccessSettingsPage() {
  const { t } = useTranslation();
  const { authorizedFetch, auth } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const isAdmin = auth.type === 'ADMIN';
  const perms = auth.permissions ?? [];
  const canEdit = isAdmin || perms.includes('UI_SETTING:UPDATE');
  const canRead = isAdmin || perms.includes('UI_SETTING:READ') || canEdit;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<GeoAccessConfigV1 | null>(null);

  const countryOptions = useMemo(() => {
    const list = countries.getNames('en', { select: 'official' }) as Record<string, string>;
    return Object.entries(list)
      .map(([code, name]) => ({ value: code, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSaved(false);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/geo-access`);
      if (!res.ok) throw new Error('load');
      const data = (await res.json()) as GeoAccessConfigV1;
      setConfig(data);
    } catch {
      setError(
        t('geo_access_load_failed', {
          defaultValue: 'Failed to load geo access settings.',
        }),
      );
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [globalAuthorizedFetch, t]);

  useEffect(() => {
    if (!canRead) return;
    void load();
  }, [canRead, load]);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/geo-access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || 'save');
      }
      const data = (await res.json()) as GeoAccessConfigV1;
      setConfig(data);
      setSaved(true);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('geo_access_save_failed', {
              defaultValue: 'Failed to save settings.',
            }),
      );
    } finally {
      setSaving(false);
    }
  };

  const addCountry = (codeRaw: string) => {
    const code = normalizeCountryCode(codeRaw);
    if (!code) return;
    setConfig((prev) => {
      if (!prev) return prev;
      if (prev.countries.some((r) => normalizeCountryCode(r.countryCode) === code)) return prev;
      return {
        ...prev,
        countries: [...prev.countries, { countryCode: code, policy: 'FULL_ACCESS' }],
      };
    });
  };

  const removeCountry = (codeRaw: string) => {
    const code = normalizeCountryCode(codeRaw);
    setConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, countries: prev.countries.filter((r) => normalizeCountryCode(r.countryCode) !== code) };
    });
  };

  const setCountryPolicy = (countryCode: string, policy: GeoAccessPolicy) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        countries: prev.countries.map((r) =>
          normalizeCountryCode(r.countryCode) === normalizeCountryCode(countryCode) ? { ...r, policy } : r,
        ),
      };
    });
  };

  const sortedRules = useMemo(() => {
    const list = config?.countries ?? [];
    return [...list].sort((a, b) => normalizeCountryCode(a.countryCode).localeCompare(normalizeCountryCode(b.countryCode)));
  }, [config?.countries]);

  const policyLabel = (p: GeoAccessPolicy) =>
    p === 'FULL_ACCESS'
      ? t('geo_access_policy_full', { defaultValue: 'Full access' })
      : p === 'NO_ORDERS'
        ? t('geo_access_policy_no_orders', { defaultValue: 'No orders' })
        : t('geo_access_policy_block_all', {
            defaultValue: 'Block entire site',
          });

  const defaultPolicyOptions: Array<{ value: GeoAccessPolicy; label: string }> = [
    { value: 'FULL_ACCESS', label: policyLabel('FULL_ACCESS') },
    { value: 'NO_ORDERS', label: policyLabel('NO_ORDERS') },
    { value: 'BLOCK_ALL', label: policyLabel('BLOCK_ALL') },
  ];

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath="/workspace/geo-access">
      <main className="workspace-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-brand-700">
              <Globe className="h-6 w-6" strokeWidth={2} />
              <h1 className="workspace-page-title">
                {t('geo_access_title', { defaultValue: 'Country access control' })}
              </h1>
            </div>
            <p className="max-w-3xl workspace-page-subtitle text-slate-600">
              {t('geo_access_subtitle', {
                defaultValue:
                  'Configure which countries can access the site, which can browse but not order, and which are fully blocked. Server-side enforcement applies to all API calls.',
              })}
            </p>
          </div>
        </div>

        {!canRead ? (
          <p className="text-sm text-slate-600">
            {t('geo_access_no_permission', {
              defaultValue: 'You do not have permission to view these settings.',
            })}
          </p>
        ) : loading ? (
          <p className="text-sm text-slate-600">{t('common.loading')}</p>
        ) : !config ? (
          <p className="text-sm text-red-600">
            {error ||
              t('geo_access_unable_load', {
                defaultValue: 'Unable to load settings.',
              })}
          </p>
        ) : (
          <div className="workspace-panel space-y-3">
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            {saved ? (
              <p className="text-sm font-medium text-emerald-700">
                {t('common.saved', { defaultValue: 'Saved.' })}
              </p>
            ) : null}

            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <h2 className="mb-3 text-lg font-semibold text-slate-900">
                {t('geo_access_global_defaults', { defaultValue: 'Global defaults' })}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700">
                    {t('geo_access_default_policy_label', {
                      defaultValue: 'Default policy (when country is unknown)',
                    })}
                  </label>
                  <Select
                    isDisabled={!canEdit || saving}
                    value={defaultPolicyOptions.find((o) => o.value === config.defaultPolicy) ?? defaultPolicyOptions[0]}
                    options={defaultPolicyOptions}
                    onChange={(opt) => {
                      const v = (opt as any)?.value as GeoAccessPolicy | undefined;
                      if (!v) return;
                      setConfig((prev) => (prev ? { ...prev, defaultPolicy: v } : prev));
                    }}
                    classNamePrefix="rs"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: 40,
                        borderRadius: 12,
                        borderColor: '#e2e8f0',
                        boxShadow: 'none',
                      }),
                    }}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!config.strictUnknownBlock}
                    onChange={(e) => setConfig((prev) => (prev ? { ...prev, strictUnknownBlock: e.target.checked } : prev))}
                    disabled={!canEdit || saving}
                  />
                  {t('geo_access_strict_mode', {
                    defaultValue:
                      'Strict mode: block entire site when country cannot be resolved',
                  })}
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!config.staffBypass}
                    onChange={(e) => setConfig((prev) => (prev ? { ...prev, staffBypass: e.target.checked } : prev))}
                    disabled={!canEdit || saving}
                  />
                  {t('geo_access_staff_bypass', {
                    defaultValue: 'Allow staff to bypass country blocks (recommended)',
                  })}
                </label>
              </div>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-slate-900">
                  {t('geo_access_country_rules', { defaultValue: 'Country rules' })}
                </h2>
                <div className="min-w-[280px]">
                  <Select
                    isDisabled={!canEdit || saving}
                    value={null}
                    options={countryOptions}
                    onChange={(opt) => {
                      if (!opt) return;
                      addCountry((opt as CountryOption).value);
                    }}
                    classNamePrefix="rs"
                    placeholder={t('geo_access_add_country_placeholder', {
                      defaultValue: 'Add country…',
                    })}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: 40,
                        borderRadius: 12,
                        borderColor: '#e2e8f0',
                        boxShadow: 'none',
                      }),
                    }}
                  />
                </div>
              </div>

              {sortedRules.length === 0 ? (
                <p className="text-sm text-slate-600">
                  {t('geo_access_no_country_rules', {
                    defaultValue: 'No country-specific rules yet.',
                  })}
                </p>
              ) : (
                <div className="space-y-3">
                  {sortedRules.map((r) => {
                    const code = normalizeCountryCode(r.countryCode);
                    return (
                      <div key={code} className="rounded-xl border border-slate-200 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-[220px]">
                            <div className="text-sm font-semibold text-slate-900">
                              {countryNameForCode(code)} <span className="font-mono text-xs text-slate-500">({code})</span>
                            </div>
                            <div className="mt-1 text-xs text-slate-600">
                              {t('geo_access_policy_prefix', { defaultValue: 'Policy:' })}{' '}
                              {policyLabel(r.policy)}
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {(['FULL_ACCESS', 'NO_ORDERS', 'BLOCK_ALL'] as GeoAccessPolicy[]).map((p) => (
                              <label
                                key={p}
                                className={[
                                  'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold',
                                  r.policy === p ? 'border-brand-600 bg-brand-50 text-brand-800' : 'border-slate-200 bg-white text-slate-700',
                                ].join(' ')}
                              >
                                <input
                                  type="radio"
                                  name={`policy_${code}`}
                                  className="h-3.5 w-3.5"
                                  checked={r.policy === p}
                                  onChange={() => setCountryPolicy(code, p)}
                                  disabled={!canEdit || saving}
                                />
                                {policyLabel(p)}
                              </label>
                            ))}
                          </div>

                          <button
                            type="button"
                            disabled={!canEdit || saving}
                            onClick={() => removeCountry(code)}
                            className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-white p-2 text-red-700 shadow-sm hover:bg-red-50 disabled:opacity-50"
                            title={t('geo_access_remove_rule', {
                              defaultValue: 'Remove rule',
                            })}
                            aria-label={t('geo_access_remove_rule', {
                              defaultValue: 'Remove rule',
                            })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {canEdit ? (
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void save()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {saving ? t('common.saving') : t('common.save')}
                </button>
              </div>
            ) : (
              <p className="text-sm text-slate-600">
                {t('common.read_only', { defaultValue: 'Read-only.' })}
              </p>
            )}
          </div>
        )}
      </main>
    </MainLayout>
  );
}

